// React and hooks
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

// Material UI
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableContainer,
  Typography,
} from '@mui/material';
import { Stack } from '@mui/system';

// Third-party components
import InfiniteScroll from 'react-infinite-scroll-component';

// Redux actions
import {
  getAllPatientByEnterprise,
  getAllPhysicianDocumentsAction,
  getFacility,
  resetAllPatientReducer,
  resetGetAllFacility,
  resetPhysicianSignDoc,
  updateDocumentStatusesAction,
} from 'src/redux/actions/physicianActions';
import { previewDashboardDoc } from 'src/redux/actions/therapist-dashboard-action';

// Types and interfaces
import physicianService, {
  PhysicianDTO,
  PhysicianDocument,
  DocumentSignatureWorkflowStatuses,
  initialPhysicianDTO,
  compositeWorkflowStatusFromDocument,
} from 'src/services/apiServices/physician-service/physician-service';
import { OptionType, SortOptions } from './types/SortOptions';

// Local components
import { Scrollbar } from '../scrollbar';
import { DocumentTableRow } from './components/DocumentTableRow';
import { DocumentTableHeader } from './components/DocumentTableHeader';
import { DocumentTableLoader } from './components/DocumentTableLoader';
import { DocumentTableEndMessage } from './components/DocumentTableEndMessage';
import SignatureModal from './components/SignatureModal';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import { singleSignatureWorkflow } from 'src/config';

type PhysicianDocumentsTableType = {
  source: string;
  signedDocumentIds?: string[];
};

function PhysicianDocumentsTableV2({
  source,
  signedDocumentIds = [],
}: PhysicianDocumentsTableType) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const statuses_pending_approval = [
    'PENDING_APPROVAL',
    'OVERDUE',
    'URGENT',
    'UPCOMING',
    'AWAITING SIGNATURE',
  ];
  const statuses_approved = ['APPROVED'];
  const statuses_signed = ['SIGNED'];
  const [isSingleSignatureWorkflow] = useFeatureToggleWhenReady(singleSignatureWorkflow, 'ENTERPRISE')

  const [physicianDto, setPhysicianDto] = useState<PhysicianDTO>(initialPhysicianDTO);
  const [selectedDocuments, setSelectedDocuments] = useState<PhysicianDocument[]>([]);
  const [pendingApprovalDocuments, setPendingApprovalDocuments] = useState<string[]>([]);
  const [approvedDocuments, setApprovedDocuments] = useState<string[]>([]);
  const [selectedDueDate, setSelectedDueDate] = useState<any>({
    startDate: '',
    endDate: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
  });
  const [isSignModalOpen, setIsSignModalOpen] = useState(false);
  const [signatureName, setSignatureName] = useState('');

  const user = useSelector((state: any) => state.getTherapistProfileReducer?.response?.data);
  const allDocumentsOfPhysician = useSelector((state: any) => state.physicianDocumentsReducer);
  const getAllFacilities = useSelector((state: any) => state.getFacilityReducer);
  const getAllPatient = useSelector((state: any) => state.getAllPatientByEnterpriseReducer);

  const getListOfValues = (items: any) => {
    return items?.map((therapist: any) => therapist?.value);
  };

  const getAllFacilityList = useCallback(
    (label: string): OptionType[] => {
      if (label === 'Patient') {
        return (
          getAllPatient?.totalPatient?.map((res: any) => ({
            label: `${res?.lastName}, ${res?.firstName}`,
            value: res?.patientId,
          })) || []
        );
      }
      if (label === 'Facility Name') {
        return (
          getAllFacilities?.totalFacility?.map((res: any) => ({
            label: `${res?.name}`,
            value: res?.uuid,
          })) || []
        );
      }
      return [];
    },
    [getAllFacilities, getAllPatient]
  );

  const getTheDates = (startDate: any, endDate: any) => {
    return {
      startDate,
      endDate,
    };
  };

  const handleCallTheList = useCallback((label: any) => {
    if (label === 'Patient') {
      dispatch(resetAllPatientReducer());
      dispatch(
        getAllPatientByEnterprise({
          searchString: '',
          modified: 'asc',
          page: 0,
          pageSize: 20,
        })
      );
      return;
    }
    // getAllPatientByEnterprise
    if (label === 'Facility Name') {
      dispatch(resetGetAllFacility());
      dispatch(
        getFacility({
          page: 0,
          searchString: '',
          pageSize: 20,
        })
      );
    }
  }, []);

  const handleSort = (sortOption: SortOptions) => {
    const key = sortOption.key;
    const old_direction = sortOption.direction || 'desc';
    const direction = old_direction === 'asc' ? 'desc' : 'asc';

    if (key === '') return;
    setPhysicianDto((prevState: any) => ({
      ...prevState,
      page: 0,
      pageSize: 20,
      sortBy: key,
      sortDirection: direction,
    }));
  };

  const handleSortMultiselect = useCallback((label: any, direction: any) => {
    setPhysicianDto((prevState: any) => ({
      ...prevState,
      page: 0,
      pageSize: 20,
      sortBy:
        label === 'Facility Name'
          ? 'facilityName'
          : label === 'Patient'
          ? 'patientName'
          : label === 'Document Type'
          ? 'documentType'
          : '',
      sortDirection: direction,
    }));
  }, []);

  const handleLoadMore = () => {
    const { patientIds, page, facilityIds, type, ...resetPhysicianDto } = physicianDto;
    const updatedPhysicianDto = {
      patientIds: getListOfValues(patientIds),
      facilityIds: getListOfValues(facilityIds),
      signatureStatus: source,
      type: getListOfValues(type),
      page: allDocumentsOfPhysician?.pageNumber,
      ...resetPhysicianDto,
    };
    dispatch(getAllPhysicianDocumentsAction(user?.providerId, updatedPhysicianDto));
  };

  const handleCallNextApi = (label: any, searchValue: any, isFromSearch: any) => {
    if (label === 'Facility Name') {
      isFromSearch && dispatch(resetGetAllFacility());
      dispatch(
        getFacility({
          page: getAllFacilities?.pageNumber,
          searchString: searchValue,
          pageSize: 20,
        })
      );
    }
    if (label === 'Patient') {
      isFromSearch && dispatch(resetAllPatientReducer());
      dispatch(
        getAllPatientByEnterprise({
          searchString: searchValue,
          modified: 'asc',
          page: getAllPatient?.pageNumber,
          pageSize: 20,
        })
      );
    }
  };

  const previewDocument = (formId: any) => {
    const { documentUrl, ...restDoc } = formId;
    dispatch(previewDashboardDoc({ ...restDoc, pdfUrl: documentUrl }));
    navigate('/physician-dashboard/doc', {
      state: {
        source: 'PHYSICIAN',
      },
    });
  };

  const handleSelect = (document: PhysicianDocument, checked: boolean) => {
    setSelectedDocuments((prev) => {
      if (checked && statuses_signed.includes(compositeWorkflowStatusFromDocument(document))) {
        setSnackbar({
          open: true,
          message: 'Document is already approved. Please select another document to sign',
        });
        return prev;
      }

      if (checked) {
        return [...prev, document];
      } else {
        return prev.filter((doc) => doc.uuid !== document.uuid);
      }
    });
  };

  const handleBulkSignature = useCallback(() => {
    if (!user?.providerId || approvedDocuments.length === 0) return;
    setIsSignModalOpen(true);
  }, [user?.providerId, approvedDocuments]);

  const handleSignConfirm = useCallback(
    async (signatureName: string) => {
      if (!user?.providerId) return;

      try {
        dispatch(
          updateDocumentStatusesAction(user.providerId, approvedDocuments, 'SIGNED', signatureName)
        );

        setIsSignModalOpen(false);

        const { patientIds, facilityIds, type, ...resetPhysicianDto } = physicianDto;
        const updatedPhysicianDto = {
          patientIds: getListOfValues(patientIds),
          facilityIds: getListOfValues(facilityIds),
          signatureStatus: source,
          type: getListOfValues(type),
          ...resetPhysicianDto,
        };

        setSelectedDocuments([]);

        dispatch(resetPhysicianSignDoc());
        // dispatch(getAllPhysicianDocumentsAction(user.providerId, updatedPhysicianDto));
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Error signing documents. Please try again later.',
        });
      }
    },
    [user?.providerId, approvedDocuments, dispatch, navigate]
  );

  const handleApprovalNavigation = (document: any) => {
    navigateToApprovalSignature();
  };

  const navigateToApprovalSignature = useCallback(() => {
    navigate('/physician-dashboard/documents-sign', {
      state: {
        selectedDocuments,
        source: 'PHYSICIAN',
      },
    });
  }, [navigate, selectedDocuments]);

  useEffect(() => {
    const { patientIds, facilityIds, type, ...resetPhysicianDto } = physicianDto;
    const updatedPhysicianDto = {
      patientIds: getListOfValues(patientIds),
      facilityIds: getListOfValues(facilityIds),
      signatureStatus: source,
      type: getListOfValues(type),
      ...resetPhysicianDto,
    };
    dispatch(resetPhysicianSignDoc());
    dispatch(getAllPhysicianDocumentsAction(user?.providerId, updatedPhysicianDto));
  }, [physicianDto]);

  useEffect(() => {
    if (allDocumentsOfPhysician?.totalDocument) {
      const pendingDocs = allDocumentsOfPhysician.totalDocument
        .filter((doc: PhysicianDocument) =>
          statuses_pending_approval.includes(compositeWorkflowStatusFromDocument(doc))
        )
        .map((doc: PhysicianDocument) => doc.uuid);
      setPendingApprovalDocuments(pendingDocs);

      const approvedDocs = allDocumentsOfPhysician.totalDocument
        .filter((doc: PhysicianDocument) =>
          statuses_approved.includes(compositeWorkflowStatusFromDocument(doc))
        )
        .map((doc: PhysicianDocument) => doc.uuid);
      setApprovedDocuments(approvedDocs);

      const initialSelectedDocs = allDocumentsOfPhysician.totalDocument.filter(
        (doc: PhysicianDocument) => {
          const status = compositeWorkflowStatusFromDocument(doc);
          return !statuses_approved.includes(status) && !statuses_signed.includes(status);
        }
      );
      setSelectedDocuments(initialSelectedDocs);
    }
  }, [allDocumentsOfPhysician?.totalDocument]);

  const hasMoreDocuments =
    allDocumentsOfPhysician?.response?.data?.totalElements !==
    allDocumentsOfPhysician?.totalDocument?.length;

  const filterProps = {
    getAllFacilityList,
    getAllPatient,
    getTheDates,
    handleCallNextApi,
    handleCallTheList,
    handleSortMultiselect,
    selectedDueDate,
    setSelectedDueDate,
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        sx={{
          position: 'relative',
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pl: 2 }}>
          <Typography variant="h5">Signature Pending</Typography>
          <Chip
            label={`for ${
              allDocumentsOfPhysician?.totalDocument?.length - approvedDocuments.length
            } / ${allDocumentsOfPhysician?.totalDocument?.length || 0} Documents`}
            color="primary"
            variant="outlined"
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, pr: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleApprovalNavigation}
            disabled={selectedDocuments.length === 0}
          >
            Approve Selected Documents ({selectedDocuments.length})
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleBulkSignature}
            disabled={approvedDocuments.length === 0}
          >
            Sign Approved Documents ({approvedDocuments.length})
          </Button>
        </Box>
      </Box>

      <SignatureModal
        open={isSignModalOpen}
        onClose={() => setIsSignModalOpen(false)}
        onSign={handleSignConfirm}
        signatureName={signatureName}
        setSignatureName={setSignatureName}
      />

      <Scrollbar>
        <Stack gap="1rem">
          <TableContainer
            component={Paper}
            sx={{ height: 700 }}
          >
            <InfiniteScroll
              dataLength={allDocumentsOfPhysician?.totalDocument?.length}
              next={handleLoadMore}
              height={600}
              hasMore={hasMoreDocuments}
              loader={<DocumentTableLoader allDocumentsOfPhysician={allDocumentsOfPhysician} />}
              endMessage={
                <DocumentTableEndMessage
                  totalDocuments={allDocumentsOfPhysician?.totalDocument?.length || 0}
                />
              }
            >
              <Table sx={{ minWidth: 700 }}>
                <DocumentTableHeader
                  physicianDto={physicianDto}
                  setPhysicianDto={setPhysicianDto}
                  onSort={handleSort}
                  getAllFacilityList={getAllFacilityList}
                  getAllPatient={getAllPatient}
                  getTheDates={getTheDates}
                  handleCallNextApi={handleCallNextApi}
                  handleCallTheList={handleCallTheList}
                  handleSortMultiselect={handleSortMultiselect}
                  selectedDueDate={selectedDueDate}
                  setSelectedDueDate={setSelectedDueDate}
                  facilitiesCount={getAllFacilities?.response?.data?.data?.totalElements}
                  patientsCount={getAllPatient?.response?.data?.data?.totalElements}
                />
                <TableBody>
                  {allDocumentsOfPhysician?.totalDocument?.map(
                    (document: any) =>
                      document.status !== 'COMPLETED' && (
                        <DocumentTableRow
                          key={document.uuid}
                          document={document}
                          onPreview={previewDocument}
                          selected={selectedDocuments.some((doc) => doc.uuid === document.uuid)}
                          onSelect={handleSelect}
                          isSignedExternally={signedDocumentIds.includes(document.uuid)}
                        />
                      )
                  )}
                </TableBody>
              </Table>
            </InfiniteScroll>
          </TableContainer>
        </Stack>
      </Scrollbar>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity="info"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PhysicianDocumentsTableV2;
